import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'

const inputClassName =
	'focus-cosy border-input bg-background placeholder:text-muted-foreground aria-[invalid]:border-input-invalid flex h-8 w-full rounded-md border px-3 py-1.5 text-body-sm file:border-0 file:bg-transparent file:text-body-sm file:font-medium disabled:cursor-not-allowed disabled:opacity-50 md:text-body-xs md:file:text-body-xs'

/** Ghost icon-button treatment for an interactive adornment (e.g. the password
 *  reveal toggle): muted, hover surface, crisp focus ring, sized to sit inside
 *  the trailing slot without crowding the control. */
const adornmentButtonClassName =
	'focus-cosy text-muted-foreground hover:text-foreground inline-flex size-6 items-center justify-center rounded-md hover:bg-[var(--bg-hover)] disabled:pointer-events-none disabled:opacity-50'

type InputProps = React.ComponentProps<'input'> & {
	/** Node rendered inside the leading (left) edge of the border — typically a
	 *  decorative glyph. */
	leadingAdornment?: React.ReactNode
	/** Node rendered inside the trailing (right) edge of the border. The password
	 *  `reveal` toggle, when enabled, owns this slot. */
	trailingAdornment?: React.ReactNode
	/** For `type="password"`: render a show/hide toggle in the trailing slot that
	 *  swaps the input `type` and its `aria-label`. */
	reveal?: boolean
}

const Input = ({
	className,
	type,
	leadingAdornment,
	trailingAdornment,
	reveal,
	...props
}: InputProps) => {
	const { t } = useTranslation()
	const [visible, setVisible] = React.useState(false)

	const revealAdornment = reveal ? (
		<button
			type="button"
			aria-label={visible ? t('field.hidePassword') : t('field.showPassword')}
			aria-pressed={visible}
			onClick={() => setVisible((shown) => !shown)}
			className={adornmentButtonClassName}
		>
			<Icon name={visible ? 'eye-off' : 'eye'} className="size-4" />
		</button>
	) : null

	const trailing = revealAdornment ?? trailingAdornment
	const resolvedType = reveal ? (visible ? 'text' : 'password') : type

	const input = (
		<input
			data-slot="input"
			type={resolvedType}
			className={cn(
				inputClassName,
				leadingAdornment && 'pl-9',
				trailing && 'pr-9',
				className,
			)}
			{...props}
		/>
	)

	if (!leadingAdornment && !trailing) return input

	return (
		<div
			data-slot="input-wrapper"
			className="relative flex w-full items-center"
		>
			{leadingAdornment ? (
				<span className="text-muted-foreground absolute inset-y-0 left-0 flex items-center pl-2">
					{leadingAdornment}
				</span>
			) : null}
			{input}
			{trailing ? (
				<span className="absolute inset-y-0 right-1 flex items-center">
					{trailing}
				</span>
			) : null}
		</div>
	)
}

export { Input }
export type { InputProps }
